import { useEffect, useRef, useState, useMemo } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import VoiceSettingsBlock from "@/components/VoiceSettingsBlock";
import {
  ChatTeardropText, FloppyDisk, Eye, MagicWand, ArrowCounterClockwise,
  PencilSimple, Check, Sparkle, Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { CUSTOMER_BILLING_ENABLED } from "@/lib/features";
import { useSector } from "@/lib/sectorContext";

/**
 * MessageSettings — strict Apple-monochrome editor for the AgentUS
 * automatic message templates.
 *
 * What the operator can do here:
 *   1. Pick a base style (formal / friendly / premium / direct)
 *   2. Tune visibility + image policy + signoff + CTA
 *   3. SELECT a kind ("Lembrete", "Cobrança", …), see the rendered
 *      preview, EDIT it inline, REWRITE with AI, and SAVE that custom
 *      wording as the new template for that kind (per tenant).
 *   4. Configure the Concierge voice (VoiceSettingsBlock).
 *
 * The preview-editor + override is THE key feature requested by the
 * operator: "if I don't want the AI, I edit it myself and save".
 */

const PAYMENT_KINDS = new Set(["billing", "receipt", "payment_link_sent", "payment_received"]);

const VALUE_OPTIONS = [
  { v: "always", l: "Sempre mostrar valor" },
  { v: "on_request", l: "Mostrar apenas se cliente perguntar" },
  { v: "never", l: "Nunca mostrar valor" },
];

const IMAGE_OPTIONS = [
  { v: "always", l: "Sempre que possível" },
  { v: "when_available", l: "Apenas quando houver imagem cadastrada" },
  { v: "never", l: "Não enviar imagem" },
];

const PREVIEW_CTX = {
  appointment_offer: { customer_name: "Rafael", asset_label: "Honda Civic 2018", service_name: "troca de óleo", slots: ["2026-05-04T09:00:00+00:00", "2026-05-04T14:00:00+00:00", "2026-05-04T17:00:00+00:00"] },
  appointment_confirmed: { customer_name: "Rafael", when: "04/05 às 14h", asset_label: "Honda Civic 2018" },
  reminder: { customer_name: "Rafael", when: "amanhã às 14h" },
  service_completed: { customer_name: "Rafael", asset_label: "Honda Civic 2018", value_cents: 85000, asset_image_url: "" },
  billing: { customer_name: "Rafael", money: "R$ 850,00", link: "https://pay.exemplo/abc", value_cents: 85000 },
  receipt: { customer_name: "Rafael", money: "R$ 850,00" },
  reactivation: { customer_name: "Rafael" },
  review_request: { customer_name: "Rafael" },
};

// v0.99.11 · Fev/2026 — Preview sector-aware. O nome do serviço e do
// "ativo" (veículo / pet / paciente / aluno / unidade) muda conforme
// o setor do tenant. Sem isso, uma academia editava mensagens com
// "Honda Civic + troca de óleo" no preview — cheirava a oficina.
// v0.99.11 · Overrides por setor. Chaves ausentes caem no default acima.
const PREVIEW_CTX_BY_SECTOR = {
  gym: {
    asset_label: "Plano Anual Premium",
    service_singular: "aula experimental",
  },
  clinic: {
    asset_label: "Consulta de retorno",
    service_singular: "consulta",
  },
  aesthetic_clinic: {
    asset_label: "Protocolo Facial",
    service_singular: "sessão",
  },
  petshop: {
    asset_label: "Thor (Golden Retriever)",
    service_singular: "banho e tosa",
  },
  salon: {
    asset_label: "Corte + Escova",
    service_singular: "escova",
  },
  accounting_firm: {
    asset_label: "Empresa: Padaria do Bairro ME",
    service_singular: "fechamento fiscal do mês",
  },
  school: {
    asset_label: "Turma 5º Ano B",
    service_singular: "reunião de pais",
  },
  condominium: {
    asset_label: "Apto 402 · Torre 2",
    service_singular: "aviso do síndico",
  },
  pharmacy: {
    asset_label: "Receita contínua — dipirona 500mg",
    service_singular: "renovação de receita",
  },
  generic: {
    asset_label: "Serviço padrão",
    service_singular: "atendimento",
  },
};

/**
 * Constrói o contexto de preview aplicando os overrides do setor
 * atual em cima do default (automotive). Mantém retro-compatibilidade
 * total: se o setor não tem override, cai no default.
 */
function buildSectorPreviewCtx(sectorCode) {
  const overrides = PREVIEW_CTX_BY_SECTOR[sectorCode];
  if (!overrides) return PREVIEW_CTX;
  const patched = {};
  for (const [kind, ctx] of Object.entries(PREVIEW_CTX)) {
    const next = { ...ctx };
    if (overrides.asset_label && "asset_label" in ctx) next.asset_label = overrides.asset_label;
    if (overrides.service_singular && "service_name" in ctx) next.service_name = overrides.service_singular;
    patched[kind] = next;
  }
  return patched;
}


// Variables the operator can drop inside an override template.
// v0.98 hotfix: nomes em PT-BR pro operador — o backend normaliza
// pros canônicos em inglês via `engines/template_vars.normalize_template`,
// garantindo backward compat com templates antigos que ainda usam
// `{first_name}`, `{customer_name}`, `{tenant_name}` etc.
const VAR_HINTS = [
  { v: "{nome_cliente}",  l: "Primeiro nome",  desc: "Primeiro nome do cliente (ex: Rafael)" },
  { v: "{nome_completo}", l: "Nome completo",  desc: "Nome cadastrado do cliente" },
  { v: "{nome_empresa}",  l: "Nome da empresa", desc: "Nome do seu negócio (ex: Omega 506)" },
  { v: "{saudacao}",      l: "Saudação",       desc: "Bom dia / Boa tarde / Boa noite (automático)" },
  { v: "{veiculo}",       l: "Veículo",        desc: "Veículo ou produto do cliente" },
  { v: "{servico}",       l: "Serviço",        desc: "Tipo de serviço (ex: troca de óleo)" },
  { v: "{data_horario}",  l: "Data/hora",      desc: "Data ou horário do agendamento" },
  { v: "{valor}",         l: "Valor",          desc: "Valor formatado (ex: R$ 850,00)" },
  { v: "{link}",          l: "Link",           desc: "Link de pagamento ou acesso" },
  { v: "{horarios}",      l: "Horários",       desc: "Horários disponíveis (ex: às 9h, 14h e 17h)" },
  { v: "{atendente}",     l: "Atendente",      desc: "Nome do atendente cadastrado" },
];

export default function MessageSettings() {
  const { profile } = useSector();
  // v0.99.11 — Preview de mensagens muda de setor pra setor.
  // Ex: academia vê "Plano Anual Premium" em vez de "Honda Civic".
  const previewCtxBySector = useMemo(
    () => buildSectorPreviewCtx(profile?.code || "automotive"),
    [profile?.code],
  );
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [meta, setMeta] = useState({ kinds: [], styles: [] });
  const [previewKind, setPreviewKind] = useState("appointment_offer");
  const [previewText, setPreviewText] = useState("");
  const [previewRaw, setPreviewRaw] = useState("");   // template with vars literal — fed into the inline editor
  const [previewImage, setPreviewImage] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [improving, setImproving] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);

  // Dirty detection for tenant-wide settings (style, signoff, etc.).
  const isDirty = settings && originalSettings &&
    JSON.stringify(settings) !== JSON.stringify(originalSettings);

  // True when this kind currently has a tenant-edited override saved.
  const hasOverride = !!(settings?.kind_overrides?.[previewKind]);

  useEffect(() => {
    (async () => {
      const [s, m] = await Promise.all([api.get("/message/settings"), api.get("/message/kinds")]);
      setSettings(s.data);
      setOriginalSettings(s.data);
      const kindsRaw = m.data?.kinds || [];
      const kinds = CUSTOMER_BILLING_ENABLED
        ? kindsRaw
        : kindsRaw.filter((k) => !PAYMENT_KINDS.has(k.kind));
      setMeta({ ...m.data, kinds });
    })();
  }, []);

  // Re-run the preview whenever settings or the selected kind change.
  // While the operator is editing the preview inline, we DON'T re-run —
  // otherwise their draft gets wiped out by background refreshes.
  useEffect(() => {
    if (!settings || editing) return;
    const t = setTimeout(runPreview, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, previewKind, editing]);

  const runPreview = async () => {
    if (!settings) return;
    try {
      const { data } = await api.post("/message/preview", {
        kind: previewKind,
        ctx: previewCtxBySector[previewKind] || {},
        settings_override: settings,
      });
      setPreviewText(data.text);
      setPreviewRaw(data.raw || "");
      setPreviewImage(data.image_url);
    } catch {
      // ignore preview errors
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/message/settings", settings);
      // Preserve client-side kind_overrides (the PUT endpoint ignores them).
      setSettings({ ...data, kind_overrides: settings.kind_overrides || {} });
      setOriginalSettings({ ...data, kind_overrides: settings.kind_overrides || {} });
      toast.success("Configurações salvas");
    } catch {
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    if (!originalSettings) return;
    setSettings({ ...originalSettings });
    toast.info("Alterações descartadas");
  };

  // ── Inline editor for the rendered preview ────────────────────
  // We seed the textarea with the RAW template (variables literal,
  // e.g. "{greeting}, {customer_name}\n\nLembrete: …{when}") instead
  // of the interpolated version. That way the operator sees and
  // controls the placeholders that will get filled in at send time.
  const startEditing = () => {
    setEditedText(previewRaw || previewText);
    setEditing(true);
    setTimeout(() => editorRef.current?.focus(), 50);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditedText("");
  };

  const insertVar = (v) => {
    const el = editorRef.current;
    if (!el) {
      setEditedText((prev) => `${prev}${prev.endsWith(" ") || !prev ? "" : " "}${v}`);
      return;
    }
    const start = el.selectionStart ?? editedText.length;
    const end = el.selectionEnd ?? editedText.length;
    const before = editedText.slice(0, start);
    const after = editedText.slice(end);
    const needsLeadingSpace = before && !/[\s\n]$/.test(before);
    const insertion = `${needsLeadingSpace ? " " : ""}${v}`;
    const next = `${before}${insertion}${after}`;
    setEditedText(next);
    // restore caret right after the inserted token
    const caret = (before + insertion).length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const rewriteWithAI = async () => {
    if (!editedText.trim()) {
      toast.info("Escreva um rascunho primeiro pra IA reescrever.");
      return;
    }
    setImproving(true);
    try {
      // Dedicated endpoint that preserves {placeholders} and does NOT
      // re-prepend a greeting — perfect for editing a kind override.
      const { data } = await api.post("/message/template/rewrite", {
        text: editedText,
        kind: previewKind,
      });
      setEditedText(data.text);
      toast.success("Texto reescrito pela IA — variáveis preservadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao reescrever");
    } finally {
      setImproving(false);
    }
  };

  const saveOverride = async () => {
    if (!editedText.trim()) {
      toast.error("Texto vazio");
      return;
    }
    setSavingOverride(true);
    try {
      const { data } = await api.put("/message/template/override", {
        kind: previewKind,
        text: editedText,
      });
      setSettings(data.settings);
      setOriginalSettings(data.settings);
      setEditing(false);
      toast.success("Template personalizado salvo");
      // re-render so the WhatsApp bubble shows the new interpolated output
      runPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar template");
    } finally {
      setSavingOverride(false);
    }
  };

  const restoreDefault = async () => {
    setSavingOverride(true);
    try {
      const { data } = await api.delete(`/message/template/override/${previewKind}`);
      setSettings(data.settings);
      setOriginalSettings(data.settings);
      setEditing(false);
      setEditedText("");
      toast.success("Template padrão restaurado");
      // re-render
      runPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao restaurar");
    } finally {
      setSavingOverride(false);
    }
  };

  if (!settings) return <Topbar title="Mensagens automáticas" subtitle="Carregando…" />;

  return (
    <>
      <Topbar
        title="Mensagens automáticas"
        subtitle="Modelos que o AgentUS envia em cada gatilho. Edite à vontade — o estilo aplica em tudo."
        actions={
          <button
            onClick={save}
            disabled={saving}
            data-testid="msg-save-btn"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 transition-colors text-[12px] font-medium shadow-sm"
          >
            <FloppyDisk size={13} weight="fill" /> {saving ? "Salvando…" : "Salvar"}
          </button>
        }
      />

      <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── ESQUERDA: tenant-wide settings ── */}
        <div className="space-y-6">
          {/* Estilo */}
          <section className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <ChatTeardropText size={14} weight="fill" className="text-slate-700" />
              <h3 className="font-heading text-sm font-semibold text-slate-900 tracking-tight">Estilo de linguagem</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {meta.styles.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSettings({ ...settings, style: s.value })}
                  data-testid={`msg-style-${s.value}`}
                  className={`text-left p-3 rounded-xl border transition-all ${
                    settings.style === s.value
                      ? "border-slate-900 bg-slate-900 text-white shadow-sm shadow-slate-900/15"
                      : "border-slate-200 hover:border-slate-400 bg-white"
                  }`}
                >
                  <div className="font-semibold text-[13px]">{s.label}</div>
                  <div className={`text-[11px] mt-0.5 leading-snug ${settings.style === s.value ? "text-slate-300" : "text-slate-500"}`}>
                    {s.value === "formal" && "Sr.(a), sem emoji, tratamento elevado"}
                    {s.value === "amigavel" && "Próximo, cordial, emoji moderado"}
                    {s.value === "premium" && "Cuidadoso, discreto, primeira pessoa do plural"}
                    {s.value === "direto" && "Sem saudação, vai ao ponto"}
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Visibilidade de valor */}
          <section className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 tracking-tight mb-3">Exibir valor</h3>
            <div className="space-y-2">
              {VALUE_OPTIONS.map((o) => (
                <label key={o.v} className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input
                    type="radio"
                    name="value_visibility"
                    checked={settings.value_visibility === o.v}
                    onChange={() => setSettings({ ...settings, value_visibility: o.v })}
                    className="w-4 h-4 text-slate-900 focus:ring-slate-900 border-slate-300"
                    data-testid={`msg-value-${o.v}`}
                  />
                  {o.l}
                </label>
              ))}
            </div>
          </section>

          {/* Imagem */}
          <section className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 tracking-tight mb-1">Política de imagem</h3>
            <p className="text-[11.5px] text-slate-500 mb-3">Aplicada em "serviço concluído" e "lembrete". Use o campo image_url do veículo para anexar foto.</p>
            <div className="space-y-2">
              {IMAGE_OPTIONS.map((o) => (
                <label key={o.v} className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input
                    type="radio"
                    name="image_policy"
                    checked={settings.image_policy === o.v}
                    onChange={() => setSettings({ ...settings, image_policy: o.v })}
                    className="w-4 h-4 text-slate-900 focus:ring-slate-900 border-slate-300"
                    data-testid={`msg-image-${o.v}`}
                  />
                  {o.l}
                </label>
              ))}
            </div>
          </section>

          {/* Atendente / CTA / signoff / emoji */}
          <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Nome do atendente (opcional)</span>
              <input
                value={settings.attendant_name || ""}
                onChange={(e) => setSettings({ ...settings, attendant_name: e.target.value })}
                placeholder='Ex: João — vira "procurar o João"'
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:border-slate-900"
                data-testid="msg-attendant-input"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                Frase final padrão <span className="normal-case tracking-normal text-slate-400 font-normal">— chamada pra ação (opcional)</span>
              </span>
              <input
                value={settings.default_cta || ""}
                onChange={(e) => setSettings({ ...settings, default_cta: e.target.value })}
                placeholder='Ex: "Posso já agendar pra você?"'
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:border-slate-900"
                data-testid="msg-cta-input"
              />
              <span className="text-[11px] text-slate-500 mt-1 block leading-relaxed">
                É a pergunta/convite no final de cada mensagem pra empurrar o cliente a responder. Em branco, a IA escolhe automaticamente.
              </span>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Assinatura final (opcional)</span>
              <input
                value={settings.company_signoff || ""}
                onChange={(e) => setSettings({ ...settings, company_signoff: e.target.value })}
                placeholder="Ex: — Equipe do seu negócio"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:border-slate-900"
                data-testid="msg-signoff-input"
              />
            </label>
            <label className="flex items-center gap-2 text-[13px] cursor-pointer">
              <input
                type="checkbox"
                checked={settings.emojis_enabled === true}
                onChange={(e) => setSettings({ ...settings, emojis_enabled: e.target.checked ? true : false })}
                className="w-4 h-4 text-slate-900 focus:ring-slate-900 border-slate-300 rounded"
                data-testid="msg-emoji-toggle"
              />
              Forçar emojis (sobrescreve padrão do estilo)
            </label>
          </section>

          {/* ── Voice (reusable block) ── */}
          <VoiceSettingsBlock
            moments={["reactivation", "post_service", "appointment", "vip", "budget_recovery", "important_confirm", "manual"]}
            intro="Em momentos especiais (reativação, pós-atendimento, VIP), o AgentUS pode mandar áudio humanizado. Se algo falhar, cai pra texto."
            testidPrefix="audio"
          />
        </div>

        {/* ── DIREITA: preview editável ── */}
        <div className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <header className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
              <Eye size={13} weight="regular" className="text-slate-700" />
              <h3 className="font-heading text-sm font-semibold text-slate-900 tracking-tight">Preview ao vivo</h3>
              {hasOverride && (
                <span
                  className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-slate-900 text-white font-bold"
                  data-testid="msg-override-badge"
                >
                  personalizado
                </span>
              )}
              <select
                value={previewKind}
                onChange={(e) => { setPreviewKind(e.target.value); setEditing(false); }}
                data-testid="msg-preview-kind"
                className="ml-auto rounded-full border border-slate-200 px-3 py-1 text-[12px] focus:outline-none focus:border-slate-900 bg-white"
              >
                {meta.kinds.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
              </select>
            </header>

            {/* WhatsApp bubble — display or edit */}
            <div className="wa-bg p-6">
              {!editing ? (
                <div className="space-y-3">
                  <div
                    className="bg-[#DCF8C6] rounded-lg px-3 py-2 max-w-[88%] ml-auto shadow-sm"
                    data-testid="msg-preview-bubble"
                  >
                    <div className="whitespace-pre-wrap text-[13.5px] text-slate-900 leading-relaxed">
                      {previewText || <span className="text-slate-400 italic">Carregando preview…</span>}
                    </div>
                    {previewImage && (
                      <img
                        src={previewImage}
                        alt="anexo"
                        className="mt-2 rounded max-h-40 object-cover"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <div className="text-[10px] text-slate-500 text-right mt-1">agora</div>
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    {hasOverride && (
                      <button
                        type="button"
                        onClick={restoreDefault}
                        disabled={savingOverride}
                        data-testid="msg-restore-default-btn"
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-[11.5px] font-medium text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors disabled:opacity-50"
                      >
                        <ArrowCounterClockwise size={11} weight="bold" />
                        Restaurar padrão
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={startEditing}
                      data-testid="msg-edit-template-btn"
                      className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3.5 py-1.5 text-[11.5px] font-semibold hover:bg-slate-800 transition-colors"
                    >
                      <PencilSimple size={11} weight="bold" />
                      Editar essa mensagem
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3" data-testid="msg-editor-pane">
                  <div className="bg-white rounded-lg border border-slate-300 px-3 py-2 max-w-[88%] ml-auto shadow-sm">
                    <textarea
                      ref={editorRef}
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      rows={Math.max(4, editedText.split("\n").length + 1)}
                      className="w-full text-[13.5px] text-slate-900 leading-relaxed bg-transparent resize-none focus:outline-none"
                      placeholder="Escreva sua versão da mensagem aqui…"
                      data-testid="msg-editor-textarea"
                    />
                    <div className="text-[10px] text-slate-400 text-right">edição manual</div>
                  </div>

                  {/* Variables — visible legend, clickable to insert */}
                  <div className="max-w-[88%] ml-auto">
                    <div className="text-[9.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">
                      Variáveis disponíveis
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {VAR_HINTS.map((h) => (
                        <button
                          key={h.v}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); insertVar(h.v); }}
                          title={h.desc}
                          data-testid={`msg-insert-var-${h.v.replace(/[{}]/g, "")}`}
                          className="group flex items-center gap-1.5 text-left px-2 py-1 rounded-md hover:bg-slate-100 transition-colors"
                        >
                          <code className="text-[10.5px] font-mono text-slate-900 bg-slate-100 group-hover:bg-slate-900 group-hover:text-white px-1.5 py-0.5 rounded transition-colors shrink-0">
                            {h.v}
                          </code>
                          <span className="text-[11px] text-slate-600 truncate">{h.l}</span>
                        </button>
                      ))}
                    </div>
                    <p className="text-[10.5px] text-slate-400 mt-1.5 leading-snug">
                      As variáveis são trocadas pelo valor real do cliente quando a mensagem for enviada.
                    </p>
                  </div>

                  {/* Action bar */}
                  <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={cancelEditing}
                      data-testid="msg-edit-cancel-btn"
                      className="text-[11.5px] text-slate-500 hover:text-slate-900 px-2 py-1.5 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={rewriteWithAI}
                      disabled={improving || !editedText.trim()}
                      data-testid="msg-rewrite-ai-btn"
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-[11.5px] font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900 transition-colors disabled:opacity-50"
                    >
                      <Sparkle size={11} weight="fill" />
                      {improving ? "Reescrevendo…" : "Reescrever com IA"}
                    </button>
                    <button
                      type="button"
                      onClick={saveOverride}
                      disabled={savingOverride || !editedText.trim()}
                      data-testid="msg-save-override-btn"
                      className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3.5 py-1.5 text-[11.5px] font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors"
                    >
                      <Check size={11} weight="bold" />
                      {savingOverride ? "Salvando…" : "Salvar como meu template"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* hint */}
            <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50/60">
              <p className="text-[11px] text-slate-500 leading-relaxed flex items-start gap-1.5">
                <Lightning size={11} weight="fill" className="mt-0.5 text-slate-400 shrink-0" />
                {hasOverride
                  ? "Esse tipo de mensagem usa SUA versão personalizada. Edite a qualquer momento ou restaure o padrão."
                  : "Versão padrão gerada pelo Message Engine. Clique em 'Editar essa mensagem' para personalizar — você pode reescrever com IA ou digitar do seu jeito."}
              </p>
            </div>
          </section>

          {/* Quick rewrite from blank — keeps the old "improve" entry point but
              monochromatic and clear. Useful when the operator wants to write
              a one-off message and check how the engine reshapes it.
              The "Salvar como template" button templatizes the IA result
              (Rafael → {first_name}, etc.) and feeds it into the inline
              editor of the selected kind. */}
          <section className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <MagicWand size={14} weight="fill" className="text-slate-700" />
              <h3 className="font-heading text-sm font-semibold text-slate-900 tracking-tight">Reescrever texto livre</h3>
            </div>
            <p className="text-[12px] text-slate-500 mb-3">
              Cole um texto qualquer e veja como o Message Engine reescreve no estilo escolhido. Você pode salvar o resultado como template do tipo de mensagem que estiver selecionado acima.
            </p>
            <FreeImprove
              currentKind={previewKind}
              kindLabel={meta.kinds.find((k) => k.kind === previewKind)?.label || previewKind}
              onApplyAsTemplate={(text) => {
                // Replace concrete demo values with their template placeholders,
                // so the operator gets a true reusable template, not a snapshot.
                const ctx = previewCtxBySector[previewKind] || {};
                let tpl = text;
                if (ctx.customer_name) {
                  const first = ctx.customer_name.split(" ")[0];
                  tpl = tpl.replaceAll(ctx.customer_name, "{customer_name}");
                  tpl = tpl.replaceAll(first, "{first_name}");
                }
                if (ctx.asset_label) tpl = tpl.replaceAll(ctx.asset_label, "{asset_label}");
                if (ctx.service_name) tpl = tpl.replaceAll(ctx.service_name, "{service_name}");
                if (ctx.when) tpl = tpl.replaceAll(ctx.when, "{when}");
                if (ctx.money) tpl = tpl.replaceAll(ctx.money, "{money}");
                if (ctx.link) tpl = tpl.replaceAll(ctx.link, "{link}");
                setEditedText(tpl);
                setEditing(true);
                setTimeout(() => editorRef.current?.focus(), 60);
                toast.info("Revise o template e clique em 'Salvar como meu template'");
                // scroll the editor into view
                document.querySelector('[data-testid="msg-preview-bubble"], [data-testid="msg-editor-pane"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
            />
          </section>
        </div>
      </div>

      {/* Sticky save bar — only appears when there are unsaved tenant-wide
          changes (style, signoff, etc.). Template overrides save via their
          own dedicated buttons. */}
      {isDirty && (
        <div
          data-testid="msg-sticky-save-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-full shadow-2xl shadow-slate-900/30 px-4 py-2.5 flex items-center gap-4 animate-in slide-in-from-bottom duration-300"
        >
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            <span className="text-[12.5px] font-medium">Alterações não salvas</span>
          </div>
          <div className="h-4 w-px bg-white/15" />
          <button
            onClick={discardChanges}
            data-testid="msg-discard-btn"
            className="text-[12px] text-slate-300 hover:text-white transition-colors"
          >
            Descartar
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="msg-sticky-save-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 px-3.5 py-1.5 text-[12px] font-semibold hover:bg-slate-100 disabled:opacity-60 transition-colors"
          >
            <FloppyDisk size={12} weight="fill" />
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Free-form improve panel. Kept as a tiny sub-component so the main
// page doesn't carry yet another piece of state. After the IA rewrites
// the draft, the operator can apply the result as a template for the
// currently-selected message kind — values like "Rafael" / "Honda Civic"
// get templatized back into {first_name} / {asset_label} by the parent.
function FreeImprove({ kindLabel, onApplyAsTemplate }) {
  const [draft, setDraft] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!draft.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/message/improve", { draft, customer_name: "Rafael" });
      setResult(data.text);
    } catch {
      toast.error("Falha ao reescrever");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder='Ex: "seu pedido tá pronto vem buscar"'
        rows="3"
        data-testid="msg-improve-input"
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:border-slate-900 font-mono"
      />
      <button
        onClick={run}
        disabled={loading || !draft.trim()}
        data-testid="msg-improve-btn"
        className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-2 text-[12.5px] font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors"
      >
        <Sparkle size={12} weight="fill" /> {loading ? "Reescrevendo…" : "Reescrever"}
      </button>
      {result && (
        <div className="mt-4 space-y-3">
          <div
            className="p-3 rounded-md bg-slate-50 border border-slate-200 text-[13px] text-slate-800 whitespace-pre-wrap"
            data-testid="msg-improve-result"
          >
            {result}
          </div>
          <div className="flex items-center justify-end gap-2">
            <span className="text-[11px] text-slate-500 mr-auto">
              Aplicar como template de <b className="text-slate-700">{kindLabel}</b>?
            </span>
            <button
              type="button"
              onClick={() => onApplyAsTemplate?.(result)}
              data-testid="msg-improve-apply-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3.5 py-1.5 text-[11.5px] font-semibold hover:bg-slate-800 transition-colors"
            >
              <Check size={11} weight="bold" />
              Salvar como template
            </button>
          </div>
        </div>
      )}
    </>
  );
}
